import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Hash, Plus, X, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import axios from 'axios';
import { RecordActions } from '../components/common/RecordActions';
import { RecordViewDialog } from '../components/common/RecordViewDialog';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerNumberRange = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('customer_number_range');
  const [viewItem, setViewItem] = useState(null);
  const [numberRanges, setNumberRanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentRange, setCurrentRange] = useState(null);
  const [formData, setFormData] = useState({
    number_range_id: '',
    description: '',
    external: false,
    from_number: '',
    to_number: '',
    prefix: ''
  });

  useEffect(() => {
    loadNumberRanges();
  }, []);

  const loadNumberRanges = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/customer-number-ranges`);
      setNumberRanges(response.data);
    } catch (error) {
      console.error('Error loading number ranges:', error);
      toast.error('Failed to load number ranges');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.number_range_id) {
      toast.error('Number Range ID is required');
      return;
    }

    if (formData.external && (!formData.prefix || formData.prefix.trim() === '')) {
      toast.error('For external ranges, Prefix is mandatory to prevent overlapping with internal numbers');
      return;
    }

    if (!formData.external && (!formData.from_number || !formData.to_number)) {
      toast.error('For internal ranges, From Number and To Number are required');
      return;
    }

    try {
      const payload = {
        ...formData,
        from_number: formData.from_number ? parseInt(formData.from_number) : null,
        to_number: formData.to_number ? parseInt(formData.to_number) : null
      };

      if (editMode) {
        const updatePayload = { ...payload };
        delete updatePayload.number_range_id;
        await axios.put(
          `${BACKEND_URL}/api/customer-number-ranges/${currentRange.number_range_id}`,
          updatePayload
        );
        toast.success('Number range updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/customer-number-ranges`, payload);
        toast.success('Number range created successfully');
      }
      
      setShowDialog(false);
      resetForm();
      loadNumberRanges();
    } catch (error) {
      console.error('Error saving number range:', error);
      toast.error(error.response?.data?.detail || 'Failed to save number range');
    }
  };

  const handleEdit = (range) => {
    setCurrentRange(range);
    setFormData({
      number_range_id: range.number_range_id,
      description: range.description || '',
      external: range.external,
      from_number: range.from_number?.toString() || '',
      to_number: range.to_number?.toString() || '',
      prefix: range.prefix || ''
    });
    setEditMode(true);
    setShowDialog(true);
  };

  const resetForm = () => {
    setFormData({
      number_range_id: '',
      description: '',
      external: false,
      from_number: '',
      to_number: '',
      prefix: ''
    });
    setCurrentRange(null);
    setEditMode(false);
  };

  const handleDialogClose = () => {
    setShowDialog(false);
    resetForm();
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <Hash className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Customer Number Ranges</h1>
                <p className="text-sm text-gray-600">Configure number ranges for customer numbering</p>
              </div>
            </div>
            {writable && (<Button
              onClick={() => setShowDialog(true)}
              className="bg-orange-600 hover:bg-orange-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Number Range
            </Button>)}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="w-10 h-10 animate-spin text-orange-600 mb-3" />
              <p className="text-sm text-gray-600">Loading number ranges...</p>
            </div>
          ) : numberRanges.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Hash className="w-16 h-16 text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-1">No number ranges yet</h3>
              <p className="text-sm text-gray-600 mb-4">Get started by creating your first customer number range</p>
              {writable && (<Button onClick={() => setShowDialog(true)} className="bg-orange-600 hover:bg-orange-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Number Range
              </Button>)}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Range ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Range</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Current</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prefix</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {numberRanges.map((range) => (
                    <tr key={range.number_range_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-medium text-gray-900">{range.number_range_id}</span>
                      </td>
                      <td className="px-6 py-4">{range.description || '-'}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded ${range.external ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'}`}>
                          {range.external ? 'External' : 'Internal'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {range.external ? (
                          <span className="text-gray-400">Manual entry</span>
                        ) : (
                          <span className="text-gray-900">{range.from_number} - {range.to_number}</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {!range.external ? (
                          <span className="px-2 py-1 bg-orange-50 text-orange-700 text-xs rounded font-semibold">
                            {range.current_number || range.from_number}
                          </span>
                        ) : (
                          <span className="text-gray-400">N/A</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {range.prefix ? (
                          <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded font-mono">
                            {range.prefix}
                          </span>
                        ) : (
                          <span className="text-gray-400">None</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <RecordActions doctype="customer_number_range" onView={() => setViewItem(range)} onEdit={() => handleEdit(range)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Create/Edit Dialog */}
        {showDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  {editMode ? 'Edit Number Range' : 'Add Number Range'}
                </h2>
                <button onClick={handleDialogClose} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Number Range ID *</Label>
                    <Input
                      maxLength={2}
                      value={formData.number_range_id}
                      onChange={(e) => setFormData({ ...formData, number_range_id: e.target.value.toUpperCase() })}
                      placeholder="2-char ID"
                      disabled={editMode}
                      className="mt-1"
                    />
                    <p className="text-xs text-gray-500 mt-1">Maximum 2 characters</p>
                  </div>

                  <div>
                    <Label>Description</Label>
                    <Input
                      maxLength={50}
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Optional"
                      className="mt-1"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="external"
                    checked={formData.external}
                    onChange={(e) => setFormData({ ...formData, external: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="external" className="cursor-pointer">
                    External Number Range (Manual entry)
                  </Label>
                </div>

                {!formData.external && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>From Number *</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.from_number}
                        onChange={(e) => setFormData({ ...formData, from_number: e.target.value })}
                        placeholder="Start number"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>To Number *</Label>
                      <Input
                        type="number"
                        min="0"
                        value={formData.to_number}
                        onChange={(e) => setFormData({ ...formData, to_number: e.target.value })}
                        placeholder="End number"
                        className="mt-1"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <Label>Prefix {formData.external && <span className="text-red-500">*</span>}</Label>
                  <Input
                    maxLength={3}
                    value={formData.prefix}
                    onChange={(e) => setFormData({ ...formData, prefix: e.target.value.toUpperCase() })}
                    placeholder={formData.external ? "Required for external ranges" : "Optional"}
                    className="mt-1"
                    required={formData.external}
                  />
                  {formData.external ? (
                    <p className="text-xs text-amber-600 mt-1 font-medium">
                      ⚠️ Mandatory for external ranges to prevent overlap with internal numbers (max 3 characters)
                    </p>
                  ) : (
                    <p className="text-xs text-gray-500 mt-1">Max 3 characters (optional for internal ranges)</p>
                  )}
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={handleDialogClose} className="flex-1">
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 bg-orange-600 hover:bg-orange-700">
                    {editMode ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default CustomerNumberRange;
